import { FC, useCallback, useEffect, ClipboardEvent, useState, useLayoutEffect, memo, useRef, useMemo } from 'react'
import styles from 'components/features/socialFeed/ComposeInput/ComposeInput.styl'
import { ComposeId, ComposeInsertTextCommand, ComposeReplaceTextCommand } from 'interfaces/feed'
import { useAutosize, useComposeState } from 'helpers/hookHelpers'
import { useComposeCommand } from 'helpers/composeHooks'
import { extractTag } from 'helpers/composeHelpers'
import { useStoreDispatch } from 'helpers/reactReduxHelpers'
import { COMPOSE_MAX_LENGTH } from 'constants/feed'
import { composeExistsActionCreator } from 'actions/feedComposeActions'
import { defaultMemoize } from 'reselect'
import { insertTextHistorySafe } from 'helpers/DOMHelpers'

export type InputSelection = Pick<HTMLTextAreaElement, 'selectionStart' | 'selectionEnd'>

type ComposeInputProps = {
  composeId: ComposeId
  value: string

  onChange: (value: string) => void
  onSelectionChange: (selection: InputSelection) => void
  onPastedFiles?: (files: Blob[]) => void
}

export const ComposeInput: FC<ComposeInputProps> = memo((props) => {
  const {
    onChange,
    onSelectionChange,
    onPastedFiles,
  } = props

  // force rerender on input reference ready to recreate handlers and let them work with proper inputNode
  const [inputNode, setInputNode] = useState<HTMLTextAreaElement | null>(null)
  const handleInputRef = useCallback<(ref: HTMLTextAreaElement | null) => void>((ref) => {
    if (inputNode !== ref) {
      setInputNode(ref)
    }
  }, [inputNode, setInputNode])

  const [value, setValue] = useState(props.value)

  const { placeholder, focusOnMount } = useComposeState(props.composeId, {
    placeholder: '',
    focusOnMount: false,
  })

  useLayoutEffect(() => {
    if (!inputNode) {
      return
    }

    if (focusOnMount) {
      inputNode.focus()
      inputNode.setSelectionRange(value.length, value.length)
    }

    inputNode.value = value

    // ! this effect should happens once when `inputNode` first time available and if `focusOnMount` is true
    // ! so don't add here other deps
  }, [inputNode])

  const handleChange = useCallback(() => {
    if (!inputNode) {
      return
    }

    setValue(inputNode.value)
  }, [inputNode])

  const handleInsertText = useCallback<(text: string) => void>((text) => {
    if (!inputNode) {
      return
    }

    const isNative = insertTextHistorySafe(inputNode, text)
    if (!isNative) {
      setValue(inputNode.value)
    }
  }, [inputNode])

  const handleInsertTextCommand = useCallback<(command: ComposeInsertTextCommand) => void>((command) => {
    if (!inputNode) {
      return
    }

    inputNode.focus()
    handleInsertText(command.text)
  }, [inputNode, handleInsertText])

  const handleStartTagCommand = useCallback(() => {
    if (!inputNode) {
      return
    }

    const { selectionStart, selectionEnd } = inputNode
    const insertText = extractTag(inputNode.value, selectionEnd, selectionStart)

    inputNode.focus()
    handleInsertText(insertText)
  }, [inputNode, handleInsertText])

  const handleReplaceTextCommand = useCallback<(command: ComposeReplaceTextCommand) => void>((command) => {
    if (!inputNode) {
      return
    }

    inputNode.focus()
    inputNode.setSelectionRange(command.start, command.end)
    handleInsertText(command.text)
  }, [inputNode, handleInsertText])

  const handleFocusCommand = useCallback(() => {
    if (!inputNode) {
      return
    }

    inputNode.focus()
  }, [inputNode])

  const handleClick = useCallback(() => {
    if (!inputNode) {
      return
    }
    inputNode.click()
  }, [inputNode])

  useComposeCommand(props, {
    insertText: handleInsertTextCommand,
    startTag: handleStartTagCommand,
    replaceText: handleReplaceTextCommand,
    focus: handleFocusCommand,
    click: handleClick,
  })

  const updateAutosize = useAutosize(inputNode)

  const onSelectionChangeCached = useCallback(defaultMemoize<(selectionStart: number, selectionEnd: number) => void>((selectionStart, selectionEnd) => {
    onSelectionChange({
      selectionStart,
      selectionEnd,
    })
  }), [onSelectionChange])

  const handleSelect = useCallback(() => {
    if (!inputNode) {
      return
    }

    const { selectionStart, selectionEnd } = inputNode
    onSelectionChangeCached(selectionStart, selectionEnd)
  }, [inputNode, onSelectionChangeCached])

  const handleSelectRef = useRef(handleSelect)
  handleSelectRef.current = handleSelect

  useEffect(() => {
    onChange(value)
    handleSelectRef.current()
  }, [value, onChange, inputNode])

  const handlePaste = useCallback<(event: ClipboardEvent<HTMLTextAreaElement>) => void>((event) => {
    if (!onPastedFiles) {
      return
    }

    const { files } = event.clipboardData

    if (files && files.length) {
      onPastedFiles(Array.from(files))
    }
  }, [onPastedFiles])

  const dispatch = useStoreDispatch()
  useEffect(() => {
    dispatch(composeExistsActionCreator(props))
  }, [])

  return useMemo(() => {
    return (
      <textarea
        ref={ handleInputRef }
        className={ styles.input }
        placeholder={ placeholder }
        maxLength={ COMPOSE_MAX_LENGTH }
        onChange={ handleChange }
        onPaste={ handlePaste }
        onSelect={ handleSelect }
        onFocus={ updateAutosize }
        rows={ 1 }
      />
    )
  }, [placeholder, handleInputRef, updateAutosize, handleSelect, handlePaste, handleChange])
})
